"use client";

import { cn } from "@/lib";
import AppContainer from "../appContainer";
import Dropdown from "../ui/dropdown/dropdown";
import { OlSort } from "solar-icon-react/ol";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useGetInquiries } from "@/hooks";
import { IRawInquiryItem } from "@/interfaces";
import { useTranslations } from "next-intl";

interface InquiryFilter {
  label: string;
  id: number;
}

const InquirySearchBarSection = () => {
  const t = useTranslations();
  const { data: inquiries } = useGetInquiries();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const categoryFilters: InquiryFilter[] = [
    { label: t("categories.bankingServices"), id: 1 },
    { label: t("categories.vehicleServices"), id: 2 },
    { label: t("categories.governmentServices"), id: 3 },
    { label: t("categories.legalServices"), id: 4 },
  ];

  const selectedCategoryId = searchParams?.get("category-id") || "";
  const selectedInquiryId = searchParams?.get("inquiry-id") || "";
  const params = new URLSearchParams(searchParams?.toString() || "");
  const allInquiryOptions = !inquiries?.data
    ? []
    : Object.entries(
        inquiries.data as Record<string, IRawInquiryItem[]>,
      ).flatMap(([categoryId, items]) =>
        items.map((item) => ({
          label: item.name,
          id: item.id.toString(),
          categoryId: Number(categoryId),
        })),
      );

  const filteredInquiryOptions = !selectedCategoryId
    ? allInquiryOptions
    : allInquiryOptions.filter(
        (option) => option.categoryId === Number(selectedCategoryId),
      );

  const handleCategoryChange = (id: string) => {
    const filter = categoryFilters.find((f) => f.label === id);
    if (filter) {
      params.set("category-id", filter.id.toString());
      params.delete("inquiry-id");
      params.set("page", "1");
      router.push(`${pathname}?${params.toString()}`);
    }
  };

  const handleInquiryChange = (id: string) => {
    const inquiry = allInquiryOptions.find((f) => f.label === id);
    if (inquiry) {
      params.set("inquiry-id", inquiry.id);
      params.set("page", "1");
      router.push(`${pathname}?${params.toString()}`);
    }
  };

  return (
    <section className={cn("relative z-10 mt-[-116px] mb-10 w-full md:mb-20")}>
      <AppContainer className={cn("h-auto")}>
        <div className="mx-auto rounded-xl bg-white p-8 shadow md:w-[80%]">
          <div
            className={cn(
              "flex flex-col justify-center gap-4 md:flex-row xl:gap-10",
            )}
          >
            <fieldset className={cn("flex w-full lg:basis-1/2")}>
              <Dropdown
                items={categoryFilters.map((filter) => filter.label)}
                value={
                  categoryFilters.find(
                    (f) => f.id.toString() === selectedCategoryId,
                  )?.label || t("dropdown.categoryPlaceholder")
                }
                onChange={handleCategoryChange}
                startIcon={
                  <span className="size-5 sm:size-6">
                    <OlSort
                      className={cn("text-custom-black-100! me-2 size-full!")}
                    />
                  </span>
                }
                variant="outline"
                className={cn("w-full", "text-xs md:text-sm lg:text-base")}
              />
            </fieldset>
            <fieldset className={cn("flex w-full lg:basis-1/2")}>
              <Dropdown
                items={filteredInquiryOptions.map((option) => option.label)}
                value={
                  filteredInquiryOptions.find((f) => f.id === selectedInquiryId)
                    ?.label || t("dropdown.inquiryPlaceholder")
                }
                onChange={handleInquiryChange}
                startIcon={
                  <span className="size-5 sm:size-6">
                    <OlSort
                      className={cn("text-custom-black-100! me-2 size-full!")}
                    />
                  </span>
                }
                variant="outline"
                className={cn("w-full", "text-xs md:text-sm lg:text-base")}
              />
            </fieldset>
          </div>
        </div>
      </AppContainer>
    </section>
  );
};

export default InquirySearchBarSection;
